import React, { ChangeEvent, useState } from 'react';

import { showToast } from '@opendoor/bricks/complex/Toast';
import { Button, Flex, Input, Text } from '@opendoor/bricks/core';
import { Form } from '@opendoor/bricks/core/Form';

import { OdProtosListingsData_Listing_State as ListingState } from '../../__generated__/athenaGlobalTypes';
import { useSetListingDetails } from '../../graphql/athena/mutations/SetListingDetails';
import { isValidDateForLwc } from '../../routes/LwcDashboard/utils';

type InContractFormProps = {
  listingId: string;
  soldPrice?: number | null;
  grossCommission?: number | null;
};

/** Set the listing details listing price, sold price, commission */
export const InContractForm: React.FC<InContractFormProps> = ({
  listingId,
  soldPrice,
  grossCommission,
}) => {
  const [date, setDate] = useState('');
  const isValidDate = isValidDateForLwc(date);
  const isoDate = isValidDate ? new Date(date).toISOString() : '';
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputDate = e.target.value;
    setDate(inputDate);
  };

  /** These names need to be in snake_case because of the way the updateMasks is set for this service */
  const [formData, setFormData] = useState({
    sold_price: soldPrice,
    gross_commission: grossCommission,
  });
  const { setListingDetails, error, loading } = useSetListingDetails();

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      // The input takes in a string, but we want to send a number to the backend
      [name]: Number.parseInt(value),
    }));
  };

  const handleSellingDetails = async (soldPrice: number, grossCommission: number, date: string) => {
    setListingDetails({
      params: {
        id: listingId,
        soldPrice: soldPrice,
        grossCommission: grossCommission,
        nextState: ListingState.IN_CONTRACT,
        updateMask: {
          paths: ['sold_price', 'gross_commission', 'next_state', 'timestamp_overrides'],
        },
        timestampOverrides: [{ state: ListingState.IN_CONTRACT, timestamp: date }],
      },
    })
      .then(() => {
        showToast('info', `Successfully set in contract details`, 'lwc-dashboard-toast}', {
          toastOptions: { autoClose: 3000 },
        });
      })
      .catch((_) => {
        // Just catch for now, log later
      });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    formData.sold_price &&
      formData.gross_commission &&
      isoDate &&
      handleSellingDetails(formData.sold_price, formData.gross_commission, isoDate);
  };

  if (error) {
    showToast('error', `Error setting selling details`, 'lwc-dashboard-toast', {
      toastOptions: { autoClose: false },
    });
  }
  return (
    <Flex flexDirection="column">
      <Form onSubmit={(e) => handleSubmit(e)}>
        <Text>What day did the home enter contract?</Text>
        <input type="date" value={date} onChange={handleDateChange} />

        <Input
          id="sold_price"
          name="sold_price"
          type="text"
          label="Sold price"
          orientation="vertical"
          value={formData.sold_price?.toString()}
          placeholder="$123,456"
          onChange={(e) => e && handleInputChange(e)}
          analyticsName="sold-price-input"
        />
        <Input
          id="gross_commission"
          name="gross_commission"
          type="text"
          label="Gross commission"
          orientation="vertical"
          value={formData.gross_commission?.toString()}
          placeholder="$123,456"
          onChange={(e) => e && handleInputChange(e)}
          analyticsName="gross-commission-input"
        />
        <Button
          my={3}
          aria-label={`Selling details`}
          analyticsName={`set-selling-details`}
          type="submit"
          disabled={loading || !isValidDate || !formData.sold_price || !formData.gross_commission}
          loading={loading}
        >
          Submit selling details
        </Button>
      </Form>
    </Flex>
  );
};
